// Standard library imports
import React, { useEffect, useState, useRef } from 'react';

// External library imports
import { Form, Formik } from 'formik';
import { toast } from 'react-toastify';
import { get as lodashGet, set as lodashSet } from 'lodash';

// Internal module imports
import { useLoader, useUserRole } from '../../../../hooks';
import { Input, ToggleButtonWithState } from '../../../../components/Inputs/Input';
import { RootBiddingService } from '../../../../services/BiddingService/Root/BiddingService';
import { momentTimeFormater } from '../../../../utils/timeHelper';
import bidClasses from '../Bidding.module.css';
import allDeviceClasses from '../../../../styles/AllDevices.module.css';
import Typography from '../../../../components/Typography/Typography';
import ModalComponent from '../../../../components/ModalComponent/ModalComponent';
import { OutOfMarket } from './OutOfMarket';
import { SecondaryBidValidator } from '../../../../validations/BiddingService/Bidding';
import { getTotalHoursForDay } from '../../../../utils/dateHelper';
import { AdminBiddingService } from '../../../../services/BiddingService/Admin/BiddingService';

const getInitialValues = (date, bidStatus = [], capacity = [], price = [], secondaryBidToggle = false, alreadyBidCreatedHours = []) => {
    const totalHours = getTotalHoursForDay(momentTimeFormater(date).format('YYYY-MM-DD'));

    const temp = {
        capacity: [],
        price: [],
    };

    Array(totalHours)
        .fill()
        .forEach((__, index) => {
            if ((bidStatus.length && lodashGet(bidStatus, [index, 'capacity'], 0) === 0) || secondaryBidToggle) {
                lodashSet(temp, ['capacity', index], alreadyBidCreatedHours.includes(index) ? 0 : lodashGet(capacity, [index], 0));
                lodashSet(temp, ['price', index], alreadyBidCreatedHours.includes(index) ? 0 : lodashGet(price, [index], 0));
            } else {
                lodashSet(temp, ['capacity', index], null);
                lodashSet(temp, ['price', index], null);
            }
        });

    return temp;
};

const CreateSecondaryBid = ({ bid, closeModal, getBids }) => {
    const { isRootUser } = useUserRole();
    const service = isRootUser ? RootBiddingService : AdminBiddingService;
    const formikRef = useRef();
    const market = bid.market;
    const [startLoader, stopLoader] = useLoader();
    const [initialValues, setInitialValues] = useState({
        capacity: [],
        price: [],
    });
    const [secondaryBidToggle, setIsSecondaryBidToggle] = useState(false);
    const [approvedCapacity, setApprovedCapacity] = useState(0);
    const [alreadyBidCreatedHours, setAlreadyBidCreatedHours] = useState([]);
    const [outOfMarketModal, setOutOfMarketModal] = useState({
        status: false,
    });
    const [outOfMarketHours, setOutOfMarketHours] = useState([]);

    const [showAllHours, setShowAllHours] = useState(false);

    const [lastBidData, setLastBidData] = useState({
        capacity: [],
        price: [],
    });
    const [bidStatus, setBidStatus] = useState(lodashGet(bid, ['primary', 'provider', 'bidStatus'], []));

    useEffect(() => {
        if (secondaryBidToggle) {
            const capAndPrice = getInitialValues(bid.date, bidStatus, lastBidData.capacity, lastBidData.price, secondaryBidToggle);
            setInitialValues(capAndPrice);
        }
    }, [secondaryBidToggle]);

    useEffect(() => {
        if (bid.customerId) {
            getLastBid();
            getOtherMarketsBids();
        }
    }, [bid.customerId]);

    useEffect(() => {
        if (alreadyBidCreatedHours.length) {
            const capAndPrice = getInitialValues(
                bid.date,
                [],
                lodashGet(initialValues, ['capacity']),
                lodashGet(initialValues, ['price']),
                secondaryBidToggle,
                alreadyBidCreatedHours
            );
            setInitialValues(capAndPrice);
        }
    }, [alreadyBidCreatedHours]);

    useEffect(() => {
        const capAndPrice = getInitialValues(bid.date, showAllHours ? [] : bidStatus, lastBidData.capacity, lastBidData.price, secondaryBidToggle, alreadyBidCreatedHours);
        setInitialValues(capAndPrice);
    }, [showAllHours]);

    useEffect(() => {
        if (outOfMarketHours.length) {
            const tempCap = [];
            const tempPrice = [];
            lodashGet(initialValues, ['capacity'], []).map((c, index) => {
                tempPrice.push(0);
                if (outOfMarketHours.includes(`HOUR__${index}`)) tempCap.push(-c);
                else tempCap.push(0);
            });
            setInitialValues({ capacity: tempCap, price: tempPrice });
        } else setInitialValues(lastBidData);
    }, [outOfMarketHours]);

    const getLastBid = () => {
        const params = {
            market,
            customerId: bid.customerId,
            instanceId: bid.instanceId,
            bidType: 'secondary',
        };
        service.GetLastBid(params, startLoader, handleGetLastBidSuccess, handleError, stopLoader);
    };

    const handleGetLastBidSuccess = ({ data }) => {
        const lastBid = lodashGet(data, ['data', 'bid']);
        setApprovedCapacity(lodashGet(data, ['data', 'approvedCapacity'], 0));

        if (lastBid) {
            const capacity = lodashGet(lastBid, ['secondary', 'capacity'], []);
            const price = lodashGet(lastBid, ['secondary', 'price'], []);
            const capAndPrice = getInitialValues(bid.date, bidStatus, capacity, price, secondaryBidToggle);
            setInitialValues(capAndPrice);
            setLastBidData({ capacity, price });
        } else {
            setInitialValues({ capacity: lodashGet(bid, ['primary', 'capacity']), price: lodashGet(bid, ['primary', 'capacity']) });
            setLastBidData({ capacity: lodashGet(bid, ['primary', 'capacity']), price: lodashGet(bid, ['primary', 'capacity']) });
        }
    };

    const getOtherMarketsBids = () => {
        service.GetOtherMarketsBid(
            { date: momentTimeFormater(bid.date).format('YYYY-MM-DD'), type: 'secondary', instanceId: bid.instanceId },
            startLoader,
            handleOtherMarketsBidSuccess,
            handleError,
            stopLoader
        );
    };

    const handleOtherMarketsBidSuccess = ({ data }) => {
        setAlreadyBidCreatedHours(lodashGet(data, ['data'], []));
    };

    const handleSubmit = (values) => {
        values.instanceId = bid.instanceId;
        values.date = bid.date;
        if (bid._id) service.CreateSecondaryBid(bid._id, values, startLoader, handleSuccess, handleError, stopLoader);
    };

    const handleSuccess = ({ res }) => {
        toast.success('Bid Created Successfully');
        getBids();
        closeModal();
    };

    const handleError = (err) => {
        toast.error(lodashGet(err, ['response', 'data', 'message']));
    };

    return (
        <div>
            <div className={bidClasses.TitleWrapper}>
                <Typography content={`${market} Secondary Bid ( ${momentTimeFormater(bid.date).format('YYYY-MM-DD')} )`} size="16" />
                {initialValues.price?.filter((e) => e != null).length > 0 && (
                    <div className={bidClasses.Flex} style={{ justifyContent: 'center' }}>
                        <div onClick={() => setOutOfMarketModal({ status: true })}>
                            <button type="button" className="btn-primary">
                                Out of Market
                            </button>
                        </div>
                        {!secondaryBidToggle && <ToggleButtonWithState value={showAllHours} onChange={(e) => setShowAllHours(e.target.checked)} label="All Hours" />}
                    </div>
                )}
            </div>
            <div>
                {initialValues.price?.filter((e) => e != null).length > 0 || secondaryBidToggle ? (
                    <div>
                        <div className={bidClasses.FormWrapper}>
                            <Formik
                                enableReinitialize
                                initialValues={initialValues}
                                onSubmit={handleSubmit}
                                validationSchema={SecondaryBidValidator(bidStatus, approvedCapacity)}
                                innerRef={formikRef}
                            >
                                {({ values, errors }) => {
                                    return (
                                        <Form>
                                            <div>
                                                {[
                                                    {
                                                        label: 'Capacity (Mw)',
                                                        value: 'capacity',
                                                    },
                                                    {
                                                        label: 'Price (€)',
                                                        value: 'price',
                                                    },
                                                ].map((type) => (
                                                    <div>
                                                        <div className={bidClasses.Subtitle}>{type.label}</div>
                                                        <div className={bidClasses.CreateBidForm}>
                                                            {lodashGet(values, [type.value], []).map(
                                                                (val, index) =>
                                                                    val !== null && (
                                                                        <div>
                                                                            <div className={allDeviceClasses.FieldControl}>
                                                                                <label htmlFor={`${type.value}.${index}`}>
                                                                                    Slot {index + 1} {' / Hour '}
                                                                                    {momentTimeFormater(bid.date).startOf('day').clone().add(index, 'hour').hour()}-
                                                                                    {momentTimeFormater(bid.date)
                                                                                        .startOf('day')
                                                                                        .clone()
                                                                                        .add(index + 1, 'hour')
                                                                                        .hour()}
                                                                                    {type.value === 'price' ? ' Price' : ' '}
                                                                                    <span className="required">*</span>
                                                                                </label>
                                                                                <Input
                                                                                    name={`${type.value}.${index}`}
                                                                                    id={`${type.value}.${index}`}
                                                                                    type={'number'}
                                                                                    disabled={alreadyBidCreatedHours.includes(index) ? true : false}
                                                                                    style={{ marginTop: '0' }}
                                                                                    step="0.1" // increasing or decreasing number by 0.1
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                    )
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </Form>
                                    );
                                }}
                            </Formik>
                        </div>
                        <div className={allDeviceClasses.ButtonContainer}>
                            <div>
                                <button type="button" className="btn-secondary" onClick={closeModal}>
                                    Cancel
                                </button>
                            </div>
                            <div>
                                <button type="submit" className="btn-primary" onClick={() => formikRef.current.submitForm()}>
                                    Submit
                                </button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div>
                        <div style={{ textAlign: 'center' }}>
                            <span>Looks like result for primary bid are not announced yet.</span>
                            <div>OR</div>
                            <span>Result were in favour for all hours.</span>
                            <div>OR</div>
                            <div className={bidClasses.Flex} style={{ justifyContent: 'center' }}>
                                <ToggleButtonWithState value={secondaryBidToggle} onChange={(e) => setIsSecondaryBidToggle(e.target.checked)} label="Primary bid not available" />
                            </div>
                        </div>
                    </div>
                )}
            </div>
            {outOfMarketModal.status && (
                <ModalComponent
                    isOpen={outOfMarketModal.status}
                    setOpen={() => {
                        setOutOfMarketModal({ status: false });
                    }}
                >
                    <OutOfMarket bid={bid} outOfMarketHours={outOfMarketHours} setOutOfMarketHours={setOutOfMarketHours} setOpen={setOutOfMarketModal} />
                </ModalComponent>
            )}
        </div>
    );
};
export default CreateSecondaryBid;
